import { useLocalization } from "@fluent/react";
import { useQueryClient } from "@tanstack/react-query";
import { useGetAutoLoanProductsQuery } from "@lending/hooks/queries/AutoLoanProduct/useGetAutoLoanProductsQuery";
import { useGetVehicleLoanApplicationQuery } from "@lending/hooks/queries/VehicleLoanApplication/useGetVehicleLoanApplicationQuery";
import { usePatchVehicleLoanApplicationQuery } from "@lending/hooks/queries/VehicleLoanApplication/usePatchVehicleLoanApplicationQuery";
import { useNavigateWithSearch } from "@lending/hooks/useNavigateWithSearch";
import { useToastsContext } from "@lending/hooks/useToasts";
import { useStore } from "../../../../contexts/StoreContext";

function useSelectedCarContent() {
  const { l10n } = useLocalization();
  const { showGenericErrorToast, showErrorToast } = useToastsContext();
  const navigate = useNavigateWithSearch();
  const { updateData } = useStore();
  const queryClient = useQueryClient();
  const { autoLoanProductsData } = useGetAutoLoanProductsQuery();

  const { data: application, isLoading: isApplicationLoading } =
    useGetVehicleLoanApplicationQuery();
  const patchVehicleLoanApplicationQuery = usePatchVehicleLoanApplicationQuery();
  const hasRefinanceProducts = autoLoanProductsData.results.some((product) => product.is_refinance);

  async function handleVehicleSelection(hasVehicleSelected: boolean, isRefinance: boolean) {
    if (!application?.id) {
      showGenericErrorToast();
      void navigate("/", { replace: true });
      return false;
    }

    try {
      await patchVehicleLoanApplicationQuery.mutateAsync({
        params: {
          path: {
            uuid: application.id,
          },
        },
        body: hasVehicleSelected
          ? {
              has_vehicle_selected: hasVehicleSelected,
              is_refinance: isRefinance,
            }
          : {
              has_vehicle_selected: hasVehicleSelected,
              is_refinance: isRefinance,
              vehicle_condition: null,
              vehicle_year: null,
              vehicle_make: null,
              vehicle_model: null,
              vehicle_trim: null,
              vehicle_mileage: null,
              vin: null,
            },
      });

      // Invalidate queries to refetch updated application
      await queryClient.invalidateQueries({
        queryKey: [
          "get",
          "/v2/lending/vehicle_loan_applications/{uuid}/",
          { params: { path: { uuid: application.id } } },
        ],
      });

      updateData({ hasVehicleSelected });
      return true;
    } catch (error) {
      console.error("Error updating vehicle selection:", error);
      showErrorToast(
        l10n.getString(
          "error-vehicle-selection-update",
          null,
          "There was an error updating your vehicle selection. Please try again."
        )
      );
      return false;
    }
  }

  return {
    data: application,
    hasRefinanceProducts,
    isApplicationLoading,
    handleVehicleSelection,
    isLoading: patchVehicleLoanApplicationQuery.isPending,
  };
}

export { useSelectedCarContent };
